import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate, useParams, useSearchParams } from 'react-router';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { defaultPerfConfig, perfConfigSchema, type PerfConfig } from '@1qa/shared';
import { ArrowLeft, Copy, FileText, Gauge, Play, Save, Trash2 } from '@/components/icons';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { formatDateTime, timeAgo } from '@/lib/format';
import { describeLoad, describeModel, VERDICT } from '@/lib/perf';
import { LoadPlanEditor } from '@/components/perf/LoadPlanEditor';
import { PerfRunDialog } from '@/components/perf/PerfRunDialog';
import { Badge, Button, Card, CardBody, CardHeader, EmptyState, ErrorNotice, Field, Input, PageHeader, PageLoader, StatusPill, Textarea, useConfirm, useToast } from '@/components/ui';

/** The first problem zod finds in a plan, in its own words ("The scenarios' shares add up to 90 %, not 100 %"). */
function planProblem(plan: PerfConfig): string | null {
  const r = perfConfigSchema.safeParse(plan);
  return r.success ? null : (r.error.issues[0]?.message ?? 'The plan is not complete');
}

/**
 * A performance test: a load plan over the project's scripts (scenarios with their share of the load, the load
 * strategy, goals) and its runs. /perf/new makes a new one (?script= preselects a script). Its creator or a Project
 * Admin changes it; "Run" opens the dialog with the capacity estimate, and the first run is usually a check.
 */
export function PerfTestPage() {
  const { projectId = '', testId } = useParams();
  const isNew = !testId;
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const { can, canModify } = useAuth();
  const project = useQuery({ queryKey: ['project', projectId], queryFn: () => api.project(projectId) });
  const test = useQuery({ queryKey: ['test', testId], queryFn: () => api.test(testId!), enabled: !isNew });
  const scripts = useQuery({ queryKey: ['perf-scripts', projectId], queryFn: () => api.perfScripts(projectId) });
  const runs = useQuery({ queryKey: ['runs', projectId], queryFn: () => api.projectRuns(projectId), refetchInterval: 10_000, enabled: !isNew });
  const [runOpen, setRunOpen] = useState(params.get('run') === '1');
  const [plan, setPlan] = useState<PerfConfig | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  // Start from the saved test, or (new) from the defaults with the chosen (or first) script as the only scenario.
  useEffect(() => {
    if (plan) return;
    if (!isNew && test.data) {
      setPlan(perfConfigSchema.safeParse(test.data.perfConfig).data ?? defaultPerfConfig());
      setName(test.data.name);
      setDescription(test.data.description ?? '');
    } else if (isNew && scripts.data) {
      const first = scripts.data.find((s) => s.id === params.get('script')) ?? scripts.data[0];
      setPlan(defaultPerfConfig(first ? [{ id: 's1', name: first.name.slice(0, 80), scriptId: first.id, share: 100, startAfterS: 0, thinkTime: 'recorded' }] : []));
      setName(first ? `${first.name} load`.slice(0, 200) : '');
    }
  }, [isNew, test.data, scripts.data, plan, params]);

  // Transactions of the scripts in use, for goals.
  const usedIds = useMemo(() => [...new Set(plan?.scenarios.map((sc) => sc.scriptId) ?? [])], [plan]);
  const full = useQueries({ queries: usedIds.map((id) => ({ queryKey: ['perf-script', id], queryFn: () => api.perfScript(id), staleTime: 60_000 })) });
  const transactionsOf = useMemo(() => new Map(full.flatMap((q) => (q.data ? [[q.data.id, q.data.script] as const] : []))), [full]);

  const save = useMutation({
    mutationFn: () =>
      isNew
        ? api.createPerfTest(projectId, { name: name.trim(), ...(description.trim() ? { description: description.trim() } : {}), perfConfig: plan! })
        : api.updateTest(testId!, { name: name.trim(), description: description.trim() || undefined, perfConfig: plan! }),
    onSuccess: (t) => {
      toast.success(isNew ? 'Performance test created. Check it with one user before you start the load.' : 'Saved');
      qc.invalidateQueries({ queryKey: ['test', t.id] });
      qc.invalidateQueries({ queryKey: ['perf-scripts', projectId] });
      if (isNew) navigate(`/projects/${projectId}/perf/${t.id}`, { replace: true });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const copy = useMutation({
    mutationFn: () => api.copyTest(testId!),
    onSuccess: (t) => navigate(`/projects/${projectId}/perf/${t.id}`),
    onError: (e) => toast.error(errorMessage(e)),
  });
  const remove = useMutation({
    mutationFn: () => api.deleteTest(testId!),
    onSuccess: () => {
      toast.success('Performance test deleted');
      navigate(`/projects/${projectId}`);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  // Old links (recorder 0.5, "Make a performance test"): making a script is its own page now.
  if (isNew && (params.has('capture') || params.has('test') || params.has('source'))) return <Navigate to={`/projects/${projectId}/perf/scripts/new${location.search}`} replace />;
  if (project.isPending || (!isNew && test.isPending) || scripts.isPending) return <PageLoader />;
  if (project.isError) return <ErrorNotice error={project.error} onRetry={() => project.refetch()} />;
  if (!isNew && test.isError) return <ErrorNotice error={test.error} onRetry={() => test.refetch()} />;
  if (scripts.isError) return <ErrorNotice error={scripts.error} onRetry={() => scripts.refetch()} />;
  if (!plan) return <PageLoader />;
  const p = project.data;
  const t = test.data;
  const mine = isNew ? can('test.write', p.orgId) : canModify('test.write', t!.createdById, p.orgId);
  const canRun = !isNew && can('perf.run', p.orgId);
  // The header describes the test as saved; the editor below may hold changes.
  const savedPlan = t ? perfConfigSchema.safeParse(t.perfConfig).data : undefined;
  const problem = plan.scenarios.length ? planProblem(plan) : 'Add a scenario: a script and its share of the load.';
  const saved = t ? JSON.stringify(savedPlan) === JSON.stringify(plan) && t.name === name.trim() && (t.description ?? '') === description.trim() : false;
  const myRuns = (runs.data ?? []).filter((r) => r.perfPlan?.testCaseId === t?.id).slice(0, 10);

  return (
    <div className="space-y-6 pb-8">
      <Link to={`/projects/${projectId}`} className="inline-flex items-center gap-1.5 text-sm text-fg-secondary hover:text-fg">
        <ArrowLeft className="size-4" /> {p.name}
      </Link>
      <PageHeader
        eyebrow="Performance test"
        title={isNew ? 'New performance test' : t!.name}
        description={
          isNew
            ? 'Choose the scripts users run, how the load builds up, and the goals.'
            : `${savedPlan ? `${savedPlan.scenarios.length} scenario${savedPlan.scenarios.length === 1 ? '' : 's'} · ${describeLoad(savedPlan.load)} · ` : ''}updated ${timeAgo(t!.updatedAt)}${t!.createdBy ? ` by ${t!.createdBy.name}` : ''}`
        }
        actions={
          !isNew && (
            <>
              {can('test.write', p.orgId) && (
                <Button variant="outline" onClick={() => copy.mutate()} loading={copy.isPending} icon={<Copy className="size-4" />}>
                  Make a copy
                </Button>
              )}
              {mine && (
                <Button
                  variant="ghost"
                  aria-label="Delete this performance test"
                  onClick={async () => {
                    if (await confirm({ title: 'Delete this performance test?', message: `“${t!.name}” and its run reports will be deleted. Its scripts stay in the library.`, confirmLabel: 'Delete' })) remove.mutate();
                  }}
                  icon={<Trash2 className="size-4" />}
                />
              )}
              {canRun && (
                <Button onClick={() => setRunOpen(true)} disabled={!saved} title={saved ? undefined : 'Save your changes first'} icon={<Play className="size-4" />}>
                  Run
                </Button>
              )}
            </>
          )
        }
      />
      {runOpen && t && (
        <PerfRunDialog
          project={p}
          test={t}
          open
          onClose={() => {
            setRunOpen(false);
            if (params.get('run')) setParams({}, { replace: true });
          }}
        />
      )}

      {!mine && <p className="rounded-card bg-surface-sunken p-4 text-sm text-fg-secondary ring-1 ring-border ring-inset">Only its creator or a Project Admin changes this test. Make a copy to change your own version.</p>}

      <Card>
        <CardBody className="grid gap-4 md:grid-cols-[1fr_1.5fr]">
          <Field label="Name">{(id) => <Input id={id} value={name} maxLength={200} disabled={!mine} onChange={(e) => setName(e.target.value)} />}</Field>
          <Field label="Description (optional)">{(id) => <Textarea id={id} rows={1} value={description} maxLength={4000} disabled={!mine} onChange={(e) => setDescription(e.target.value)} className="min-h-0" />}</Field>
        </CardBody>
      </Card>

      <LoadPlanEditor projectId={projectId} plan={plan} onChange={setPlan} scripts={scripts.data} transactionsOf={transactionsOf} readOnly={!mine} />

      {mine && (
        <div className="flex flex-wrap items-center justify-end gap-3">
          {problem && (
            <span role="status" className="text-sm text-warning">
              {problem}
            </span>
          )}
          {!isNew && saved && <span className="text-sm text-fg-muted">All changes saved</span>}
          <Button onClick={() => save.mutate()} disabled={!!problem || !name.trim() || (!isNew && saved)} loading={save.isPending} icon={<Save className="size-4" />}>
            {isNew ? 'Create performance test' : 'Save'}
          </Button>
        </div>
      )}

      {!isNew && (
        <Card>
          <CardHeader title="Recent runs" />
          {myRuns.length === 0 ? (
            <EmptyState icon={<Gauge className="size-6" />} title="Not run yet" description="Start with a check run (each scenario once, one user): it shows whether every step works before you add load." />
          ) : (
            <ul className="divide-y divide-border-subtle">
              {myRuns.map((r) => {
                const verdict = (r.summary as { verdict?: keyof typeof VERDICT }).verdict;
                return (
                  <li key={r.id}>
                    <Link to={`/runs/${r.id}`} className="flex flex-wrap items-center gap-3 px-5 py-3 hover:bg-surface-raised">
                      {verdict ? <Badge tone={VERDICT[verdict].tone}>{r.perfPlan?.verify ? (verdict === 'PASS' ? 'Check passed' : 'Check failed') : VERDICT[verdict].label}</Badge> : <StatusPill status={r.status} />}
                      <span className="text-sm text-fg">{r.perfPlan ? describeModel(r.perfPlan.model) : ''}</span>
                      <span className="ml-auto text-xs text-fg-muted">
                        {formatDateTime(r.queuedAt)}
                        {r.triggeredBy ? ` · ${r.triggeredBy.name}` : ''}
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
      )}

      {usedIds.length > 0 && (
        <p className="flex flex-wrap items-center gap-2 text-sm text-fg-secondary">
          <FileText className="size-4 text-primary" aria-hidden /> Scripts:
          {usedIds.map((id) => {
            const s = scripts.data.find((x) => x.id === id);
            return s ? (
              <Link key={id} to={`/projects/${projectId}/perf/scripts/${id}`} className="text-link hover:underline">
                {s.name}
              </Link>
            ) : null;
          })}
        </p>
      )}
    </div>
  );
}
